/* ---------------------------------------------------------------------------
   GIFT WRAP

   Product page: reveals the option, and rewrites the add-to-cart form into a
   two line `items[]` payload so one /cart/add.js call creates both the product
   line and its wrap line.

   Cart and drawer: keeps each wrap line's quantity equal to the quantity of the
   line it belongs to, and removes wrap lines whose parent has gone.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var MAX_NOTE = 200;

  /* ---------- product page ---------- */

  function panelFor(block) {
    return block.querySelector('[data-gift-wrap-panel]');
  }

  function initBlock(block) {
    if (block.giftWrapReady) return;
    block.giftWrapReady = true;

    // Only shown once the script is running: without it the wrap line could
    // not be added and the option would promise something never charged for.
    block.removeAttribute('hidden');

    var toggle = block.querySelector('[data-gift-wrap-toggle]');
    var panel = panelFor(block);
    var note = block.querySelector('[data-gift-wrap-note]');
    var count = block.querySelector('[data-gift-wrap-count]');

    if (toggle && panel) {
      // Mirrors the toggle onto the product form so browsers without :has()
      // still hide "Buy it now" while wrap is selected. See gift-wrap.css:
      // that button bypasses the form entirely and would lose the wrap line.
      var productForm = block.closest('.product-form');

      var sync = function () {
        panel.hidden = !toggle.checked;
        if (productForm) {
          productForm.classList.toggle('product-form--gift-wrap-on', toggle.checked);
        }
      };

      toggle.addEventListener('change', sync);
      sync(); // the browser may have restored a checked box on back/reload
    }

    if (note && count) {
      var template = count.dataset.template || '[used] / 200';
      var render = function () {
        count.textContent = template.replace('[used]', String(note.value.length));
      };
      note.addEventListener('input', function () {
        if (note.value.length > MAX_NOTE) note.value = note.value.slice(0, MAX_NOTE);
        render();
      });
      render();
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-gift-wrap]').forEach(initBlock);
  }

  /* ---------- add to cart ---------- */

  function hidden(form, name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
    return input;
  }

  // Runs in the capture phase on `document`, so it is finished before the
  // listener product-form.js binds on the form itself. That handler builds its
  // FormData from the form as we leave it, which is why the payload is written
  // as form inputs rather than sent separately: Dawn keeps doing the fetch,
  // the section re-render, the drawer and the error handling.
  // Dawn's quantity input lives outside the <form> and is tied to it with a
  // form="" attribute, so it is not a descendant and querySelector misses it.
  // form.elements does include form-associated controls.
  function control(form, name) {
    var el = form.elements ? form.elements[name] : null;
    if (!el) el = form.querySelector('[name="' + name + '"]');
    if (el && !el.tagName && typeof el.length === 'number') el = el[0]; // RadioNodeList
    return el || null;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.getAttribute('action') !== window.routes.cart_add_url) return;

      var block = form.querySelector('[data-gift-wrap]');
      if (!block) return;

      var toggle = block.querySelector('[data-gift-wrap-toggle]');
      if (!toggle || !toggle.checked) return;

      var design = block.querySelector('[data-gift-wrap-variant]:checked');
      if (!design) return;

      var idInput = control(form, 'id');
      var qtyInput = control(form, 'quantity');
      if (!idInput) return;

      var variantId = idInput.value;
      var quantity = qtyInput && qtyInput.value ? parseInt(qtyInput.value, 10) : 1;
      if (!quantity || quantity < 1) quantity = 1;

      var noteField = block.querySelector('[data-gift-wrap-note]');
      var noteText = noteField ? noteField.value.trim().slice(0, MAX_NOTE) : '';

      // `id` and `quantity` cannot travel with `items[]`, and the controls in
      // the block are UI only, so everything is disabled for the one tick in
      // which Dawn reads the form, then put back.
      var parked = [idInput];
      if (qtyInput) parked.push(qtyInput);
      block.querySelectorAll('input, textarea, select').forEach(function (el) {
        parked.push(el);
      });
      parked.forEach(function (el) {
        el.disabled = true;
      });

      var payload = [];
      payload.push(hidden(form, 'items[0][id]', variantId));
      payload.push(hidden(form, 'items[0][quantity]', quantity));
      payload.push(hidden(form, 'items[0][properties][_gift_wrap]', design.dataset.design));
      if (noteText) payload.push(hidden(form, 'items[0][properties][Gift note]', noteText));

      payload.push(hidden(form, 'items[1][id]', design.value));
      payload.push(hidden(form, 'items[1][quantity]', quantity));
      payload.push(hidden(form, 'items[1][properties][Wrap for]', block.dataset.productTitle || ''));
      payload.push(hidden(form, 'items[1][properties][_wrap_parent]', variantId));

      // The event dispatch is synchronous, so this runs once every listener,
      // Dawn's included, has read the form.
      setTimeout(function () {
        parked.forEach(function (el) {
          el.disabled = false;
        });
        payload.forEach(function (el) {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
        if (toggle) {
          toggle.checked = false;
          // Assigning .checked fires nothing, so the listeners are told here.
          // sync() closes the panel and drops the class that hides "Buy it
          // now"; without this the button would stay hidden after adding to
          // cart on browsers that rely on the class rather than :has().
          // Not bubbled: the listener sits on the toggle itself, and the form
          // has other change listeners that have no business seeing this.
          toggle.dispatchEvent(new Event('change', { bubbles: false }));
        }
        var panel = panelFor(block);
        if (panel) panel.hidden = true;
        if (noteField) noteField.value = '';
      }, 0);
    },
    true
  );

  /* ---------- cart and drawer ---------- */

  var busy = false;
  var passes = 0;

  // A wrap line belongs to one product line: same variant, same design. Keying
  // on both keeps two wrapped lines of the same variant in different designs
  // from being counted against each other.
  function parentKey(item) {
    var props = item.properties || {};
    return props._gift_wrap ? item.variant_id + '|' + props._gift_wrap : null;
  }

  function wrapKey(item) {
    var props = item.properties || {};
    return props._wrap_parent ? props._wrap_parent + '|' + (item.variant_title || '') : null;
  }

  function reconcile() {
    if (busy) return;
    if (passes > 12) return; // never spin, however odd the cart looks

    var host = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (!host || typeof host.updateQuantity !== 'function') return;

    fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var wanted = {};
        cart.items.forEach(function (item) {
          var key = parentKey(item);
          if (key) wanted[key] = (wanted[key] || 0) + item.quantity;
        });

        // One change per pass: updateQuantity re-renders and publishes, which
        // brings us back here with fresh line numbers.
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var key = wrapKey(item);
          if (!key) continue;
          var target = wanted[key] || 0;
          if (target === item.quantity) continue;

          busy = true;
          passes += 1;
          host.updateQuantity(i + 1, target, { currentTarget: null });
          setTimeout(function () {
            busy = false;
          }, 60);
          return;
        }

        passes = 0; // nothing left to fix
      })
      .catch(function () {
        busy = false;
      });
  }

  /* ---------- wiring ---------- */

  // cart.js is deferred too, so <cart-items> may still be an un-upgraded element
  // when we run: updateQuantity would not exist yet and reconcile() would bail,
  // leaving an orphaned wrap line until the next cart action. Wait for the
  // custom element to be defined before the first pass.
  function reconcileWhenReady() {
    var tag = document.querySelector('cart-drawer-items')
      ? 'cart-drawer-items'
      : document.querySelector('cart-items')
        ? 'cart-items'
        : null;
    if (!tag) return;

    if (window.customElements && typeof customElements.whenDefined === 'function') {
      customElements.whenDefined(tag).then(function () {
        setTimeout(reconcile, 0);
      });
    } else {
      setTimeout(reconcile, 300);
    }
  }

  function start() {
    initAll();
    reconcileWhenReady();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(reconcile, 80);
    });
  }
})();
